/**
 * Projektion: aus der Folge unveränderlicher Events (events.ts) den aktuellen
 * Bestell-Zustand (Order) berechnen. Reine Funktion, kein I/O — wird sowohl vom Server
 * (Read-Model in SQLite) als auch von der PWA (lokale Anzeige) genutzt.
 *
 * Die server-seitige Bon-/Sequenznummer (seqNo) ist NICHT Teil der Projektion — sie wird
 * vom Server vergeben und über `seqNo` durchgereicht.
 */

import type { EventEnvelope } from "./events.js";
import type { Order, OrderLine, OrderPayment } from "./model/order.js";
import { isFullyPaid } from "./model/order.js";
import type { VenueId, TableId, UserId } from "./ids.js";

/**
 * Wendet ein einzelnes Event auf den bisherigen Zustand an.
 * `prev` ist null, wenn die Order noch nicht existiert.
 *
 * Robustheit: Treffen Mutations-Events vor `order.created` ein (z. B. unsortierter Sync),
 * wird eine minimale Order-Hülle erzeugt, damit nichts verloren geht. Im Normalfall
 * (PWA sendet ihre Outbox in HLC-Reihenfolge) kommt `order.created` zuerst.
 */
export function applyOrderEvent(prev: Order | null, env: EventEnvelope): Order {
  const b = env.body;

  // Konfig-Snapshots gehören nicht in die Bestell-Projektion (siehe Store.applyConfigSnapshot).
  if (b.kind === "config.snapshot") {
    throw new Error("applyOrderEvent: config.snapshot ist kein Order-Event");
  }

  const shell = (): Order => ({
    id: b.orderId,
    venueId: env.venueId,
    tableRef: "" as TableId,
    waiterId: "" as UserId,
    status: "open",
    openedAtMs: env.hlc.wallMs,
    lines: [],
  });

  const order: Order = prev ? { ...prev, lines: [...prev.lines] } : shell();

  switch (b.kind) {
    case "order.created":
      order.tableRef = b.tableRef;
      order.waiterId = b.waiterId;
      order.openedAtMs = env.hlc.wallMs;
      if (order.status !== "paid" && order.status !== "void") order.status = "open";
      return order;

    case "line.added":
      order.lines.push(b.line);
      return order;

    case "line.voided": {
      order.lines = order.lines.map<OrderLine>((l) =>
        l.id === b.lineId ? { ...l, status: "voided" } : l,
      );
      return order;
    }

    case "order.sent":
      if (order.status === "open") order.status = "sent";
      return order;

    case "order.numbered":
      // Vom autoritativen Server vergebene Bon-Nummer nur übernehmen (nie selbst vergeben).
      order.seqNo = b.seqNo;
      return order;

    case "table.changed":
      order.tableRef = b.tableRef;
      return order;

    case "order.paid":
      order.status = "paid";
      return order;

    case "payment.recorded": {
      const payment: OrderPayment = {
        paymentId: b.paymentId,
        items: b.items,
        amountCents: b.amountCents,
        tipCents: b.tipCents,
        method: b.method,
        atMs: b.atMs,
      };
      order.payments = [...(order.payments ?? []), payment];
      if (isFullyPaid(order)) order.status = "paid";
      return order;
    }

    case "payment.numbered": {
      // Vom autoritativen Server vergebene Belegnummer nur übernehmen (nie selbst vergeben).
      order.payments = (order.payments ?? []).map((p) =>
        p.paymentId === b.paymentId ? { ...p, belegNo: b.belegNo } : p,
      );
      return order;
    }

    case "item.served":
      if (!(order.served ?? []).some((s) => s.lineId === b.lineId)) {
        order.served = [...(order.served ?? []), { lineId: b.lineId, atMs: b.atMs }];
      }
      return order;

    case "items.served":
      for (const lineId of b.lineIds) {
        if (!(order.served ?? []).some((s) => s.lineId === lineId)) {
          order.served = [...(order.served ?? []), { lineId, atMs: b.atMs }];
        }
      }
      return order;

    case "items.unserved":
      order.served = (order.served ?? []).filter((s) => !b.lineIds.includes(s.lineId));
      return order;

    case "lines.sent":
      for (const lineId of b.lineIds) {
        if (!(order.sentLines ?? []).some((s) => s.lineId === lineId)) {
          order.sentLines = [...(order.sentLines ?? []), { lineId, atMs: b.atMs }];
        }
      }
      return order;

    case "order.voided":
      order.status = "void";
      return order;

    case "print.requested":
      // Druck-Fakt (Bon/Beleg angefordert) — ändert den Bestell-Zustand nicht; nur fürs
      // Log/die Cloud-Anzeige (+ lokal-serverseitiger Nachdruck, siehe Store.projectInto).
      return order;
  }
}

/** Vollständige Order aus allen ihren Events neu aufbauen (Reparatur/Initialaufbau). */
export function projectOrder(events: EventEnvelope[]): Order | null {
  let order: Order | null = null;
  for (const env of events) {
    if (env.body.kind === "config.snapshot") continue;
    order = applyOrderEvent(order, env);
  }
  return order;
}
